import React, { useMemo, useRef, useState } from "react";
import {
  WORDS,
  UNITS,
  PARTICLES,
  unlockedWords,
  unlockedPhrases,
  shuffle,
} from "../lib/tokiPonaData";
import { parseSentence, unlockedPatternIds, matchPhrase, completions } from "../lib/tokiPonaCompose";
import { SpeakButton } from "./tokiPonaUi";
import { WordPopover } from "./TokiPonaTapWord";

/* ============================================================
   nasin toki — the sentence lab (compose sandbox)
   ------------------------------------------------------------
   A free-composition screen: tap any of your unlocked words (plus
   the particles whose grammar you've met) into a sentence, and the
   engine in src/lib/tokiPonaCompose reflects back every reasonable
   reading, ranked — deliberately broad glosses, ambiguity included.
   No score, no hearts: it's a toy for developing a feel for how
   toki pona composes meaning.

   Building affordances:
   - tiles insert at a visible caret; tap the gap between two tiles
     to move it (so pi can be dropped into an existing phrase)
   - a "try saying" prompt drawn from the unlocked phrase bank gives
     the blank row somewhere to start
   - the last-tapped word shows its dictionary gloss inline; tapping
     that line opens the shared word popover with the full note
   - typing into the compose-type box surfaces predictive completions
     (see completions() in tokiPonaCompose.js); Enter/Space/Tab commit
     or cycle the highlighted suggestion, Backspace on an empty input
     deletes the tile just before the caret
   ============================================================ */

// Friendly labels for the engine's reading tags, shown on alternates
// so a learner can tell WHY the same tiles read two ways.
const TAG_LABELS = {
  preverb: "preverb reading",
  verbal: "action reading",
  copular: "describing reading",
  prepHead: "preposition reading",
  become: "becoming reading",
  fragment: "just a phrase — not a full sentence",
  imperative: "a request",
  anu: "a choice",
  interjection: "pure feeling",
};
const tagLine = (tags) =>
  tags
    .map((t) => TAG_LABELS[t])
    .filter(Boolean)
    .join(" · ");

// One-line reminders for the particle tiles (particles aren't in WORDS,
// so the word popover can't cover them).
const PARTICLE_INFO = {
  li: "links a subject to its predicate",
  e: "marks what the action lands on",
  la: "sets the scene — context comes first",
  pi: "groups the next words into one modifier",
  en: "and — joins subjects together",
  o: "a request, or calling out to someone",
  a: "pure feeling — emphasis, delight, a sigh",
  anu: "or — offers a choice",
};

const ComposeLab = ({ completed }) => {
  const unlocked = useMemo(() => unlockedWords(completed), [completed]);
  const unlockedSet = useMemo(() => new Set(unlocked), [unlocked]);
  const patterns = useMemo(() => unlockedPatternIds(completed), [completed]);
  const prompts = useMemo(() => shuffle(unlockedPhrases(unlocked)), [unlocked]);

  const [tiles, setTiles] = useState([]);
  const [cursor, setCursor] = useState(0);
  const [showAlts, setShowAlts] = useState(false);
  const [lastTok, setLastTok] = useState(null);
  const [pop, setPop] = useState(null);
  const [promptI, setPromptI] = useState(0);
  const [typed, setTyped] = useState("");
  const [activeS, setActiveS] = useState(0);
  const nextId = useRef(0);
  const composeRef = useRef(null);
  const typeRef = useRef(null);

  const sentence = tiles.map((t) => t.tok).join(" ");
  const result = useMemo(
    () => (tiles.length ? parseSentence(sentence, { patterns }) : null),
    [sentence, tiles.length, patterns]
  );

  const add = (tok) => {
    setTiles((ts) => [
      ...ts.slice(0, cursor),
      { id: nextId.current++, tok },
      ...ts.slice(cursor),
    ]);
    setCursor((c) => c + 1);
    setLastTok(tok);
    setShowAlts(false);
    setPop(null);
  };
  const remove = (id, idx) => {
    setTiles((ts) => ts.filter((t) => t.id !== id));
    setCursor((c) => (idx < c ? c - 1 : c));
    setShowAlts(false);
  };
  const clear = () => {
    setTiles([]);
    setCursor(0);
    setShowAlts(false);
  };

  // Commit a typed suggestion as a tile, same as tapping it from the bank,
  // then clear the input and keep focus there for the next word.
  const commit = (tok) => {
    add(tok);
    setTyped("");
    setActiveS(0);
    if (typeRef.current) typeRef.current.focus();
  };

  // Word popover, positioned inside the (relatively-positioned) lab.
  const openPop = (word, rect) => {
    const el = composeRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const POP_W = 250;
    const left = Math.min(Math.max(rect.left - r.left, 0), r.width - POP_W);
    const top = rect.bottom - r.top + 6;
    setPop((cur) => (cur && cur.word === word ? null : { word, left, top }));
  };

  // Particle tiles appear once the grammar pattern that teaches them is
  // unlocked (the pattern ids match the particle spellings).
  const particles = PARTICLES.filter((p) => patterns.has(p));
  const wordGroups = UNITS.map((u) => ({
    unit: u,
    words: u.words.filter((w) => unlockedSet.has(w)),
  })).filter((g) => g.words.length);

  // typing affordance: predictive completions over unlocked words + particles
  const suggestions = useMemo(
    () => completions(typed, [...particles, ...unlocked]),
    [typed, particles, unlocked]
  );
  const lockedNote = useMemo(() => {
    const norm = typed.toLowerCase().replace(/[^a-z]/g, "");
    if (!norm || suggestions.length) return null;
    const known = [...Object.keys(WORDS), ...PARTICLES].some((w) => w.startsWith(norm));
    return known
      ? "that word joins the shelf as you learn it in lessons"
      : "not a toki pona word (yet!)";
  }, [typed, suggestions]);

  const onTypeChange = (e) => {
    setTyped(e.target.value);
    setActiveS(0);
  };
  const onTypeKeyDown = (e) => {
    if (e.key === "Enter") {
      if (suggestions.length) {
        e.preventDefault();
        commit(suggestions[activeS]);
      }
      return;
    }
    if (e.key === " ") {
      if (!typed.trim()) {
        e.preventDefault();
        return;
      }
      if (suggestions.length) {
        e.preventDefault();
        commit(suggestions[activeS]);
      }
      return;
    }
    if (e.key === "Tab") {
      if (suggestions.length > 1) {
        e.preventDefault();
        setActiveS((i) => (i + 1) % suggestions.length);
      }
      return;
    }
    if (e.key === "Backspace") {
      if (typed === "" && cursor > 0) {
        e.preventDefault();
        const t = tiles[cursor - 1];
        remove(t.id, cursor - 1);
      }
      return;
    }
    if (e.key === "Escape") {
      setTyped("");
      setActiveS(0);
    }
  };

  const top = result && result.readings[0];
  const alts = result ? result.readings.slice(1) : [];

  const prompt = prompts.length ? prompts[promptI % prompts.length] : null;
  const promptMatch = useMemo(
    () => (prompt && tiles.length ? matchPhrase(sentence, prompt) : null),
    [sentence, prompt, tiles.length]
  );
  const PROMPT_LABELS = {
    exact: "pona a! that says it —",
    equivalent: "pona a! same idea, your way —",
    close: "close — compare your reading:",
  };
  const promptClass =
    promptMatch === "exact" || promptMatch === "equivalent"
      ? "matched"
      : promptMatch === "close"
      ? "close"
      : "";
  const promptLabel = PROMPT_LABELS[promptMatch] || "try saying:";

  const gap = (i) => (
    <button
      key={`gap-${i}`}
      className={`compose-gap ${cursor === i ? "active" : ""}`}
      onClick={(e) => {
        e.stopPropagation();
        setCursor(i);
      }}
      aria-label={`insert position ${i + 1}`}
    />
  );

  return (
    <div className="compose" ref={composeRef}>
      <p className="compose-intro">
        Build anything from the words you've unlocked. The readings below stay
        deliberately broad — <i>tomo tawa</i> is a “moving structure”, not a
        car — and when your sentence can be read more than one way, you'll see
        every way. That's not a bug; that's toki pona.
      </p>

      {prompt && (
        <div className={`compose-prompt ${promptClass}`}>
          <span className="lbl">{promptLabel}</span>
          <span className="en">“{prompt.en}”</span>
          <button
            className="another"
            onClick={() => setPromptI((i) => i + 1)}
          >
            another
          </button>
        </div>
      )}

      <div className="compose-sticky">
        <div
          className="build-answer compose-row"
          onClick={() => setCursor(tiles.length)}
        >
          {tiles.length === 0 && (
            <span className="build-hint">tap words below to build…</span>
          )}
          {tiles.map((t, i) => (
            <React.Fragment key={t.id}>
              {gap(i)}
              <button
                className="tok"
                onClick={(e) => {
                  e.stopPropagation();
                  remove(t.id, i);
                }}
              >
                {t.tok}
              </button>
            </React.Fragment>
          ))}
          {tiles.length > 0 && gap(tiles.length)}
        </div>
        {tiles.length > 0 && (
          <div className="compose-tools">
            <SpeakButton text={sentence} label="hear the sentence" />
            <button className="compose-clear" onClick={clear}>
              clear
            </button>
          </div>
        )}

        {lastTok && (
          <div className="compose-lastword">
            {WORDS[lastTok] ? (
              <button
                onClick={(e) =>
                  openPop(lastTok, e.currentTarget.getBoundingClientRect())
                }
              >
                <b>{lastTok}</b> · {WORDS[lastTok].g}
                <span className="more"> ⓘ</span>
              </button>
            ) : (
              <span>
                <b>{lastTok}</b> · {PARTICLE_INFO[lastTok]}
              </span>
            )}
          </div>
        )}

        <div className="compose-type">
          <input
            ref={typeRef}
            value={typed}
            onChange={onTypeChange}
            onKeyDown={onTypeKeyDown}
            placeholder="or type a word…"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            inputMode="text"
            aria-label="type a toki pona word"
          />
          {suggestions.length > 0 && (
            <div className="compose-suggest">
              {suggestions.map((s, i) => (
                <button
                  key={s}
                  className={`tok ${PARTICLES.includes(s) ? "particle " : ""}${i === activeS ? "active" : ""}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    commit(s);
                  }}
                >
                  {s}
                </button>
              ))}
            </div>
          )}
          {lockedNote && <div className="compose-type-note">{lockedNote}</div>}
        </div>

        {result && result.errors.length > 0 && (
          <div className="compose-error">
            <div className="msg">{result.errors[0].message}</div>
            {result.errors[0].hint && (
              <div className="hint">{result.errors[0].hint}</div>
            )}
          </div>
        )}

        {top && (
          <div className="compose-readings">
            <div className="compose-top">
              <span className="lbl">
                {top.tags.includes("fragment") ? "a phrase so far" : "reads as"}
              </span>
              <div className="gloss">{top.gloss}</div>
              {tagLine(top.tags) && <div className="tags">{tagLine(top.tags)}</div>}
            </div>
            {(top.notes || []).map((n) => (
              <div className="compose-note" key={n}>
                {n}
              </div>
            ))}
            {alts.length > 0 && !showAlts && (
              <button className="compose-more" onClick={() => setShowAlts(true)}>
                {alts.length === 1
                  ? "1 more way to read this"
                  : `${alts.length} more ways to read this`}{" "}
                ›
              </button>
            )}
            {showAlts &&
              alts.map((r) => (
                <div className="compose-alt" key={r.gloss}>
                  <div className="gloss">{r.gloss}</div>
                  {tagLine(r.tags) && <div className="tags">{tagLine(r.tags)}</div>}
                </div>
              ))}
          </div>
        )}
      </div>

      {unlocked.length === 0 ? (
        <div className="compose-empty">
          Finish your first lesson and your new words will show up here,
          ready to build with.
        </div>
      ) : (
        <div className="compose-bank">
          {particles.length > 0 && (
            <div className="compose-group">
              <div className="lbl">little words</div>
              <div className="toks">
                {particles.map((p) => (
                  <button key={p} className="tok particle" onClick={() => add(p)}>
                    {p}
                  </button>
                ))}
              </div>
            </div>
          )}
          {wordGroups.map(({ unit, words }) => (
            <div className="compose-group" key={unit.id}>
              <div className="lbl">{unit.title}</div>
              <div className="toks">
                {words.map((w) => (
                  <button key={w} className="tok" onClick={() => add(w)}>
                    {w}
                  </button>
                ))}
              </div>
            </div>
          ))}
          <div className="compose-foot">
            {PARTICLES.some((p) => !patterns.has(p))
              ? `More words — and little words like ${PARTICLES.filter(
                  (p) => !patterns.has(p)
                ).join(", ")} — join the shelf as you learn them in lessons.`
              : "More words join the shelf as you learn them in lessons."}
          </div>
        </div>
      )}

      <WordPopover tap={pop} onClose={() => setPop(null)} />
    </div>
  );
};

export default ComposeLab;
